import { Alert, Button, Checkbox, Col, Drawer, Modal, ModalFuncProps, notification, Row, Skeleton, Space, Tooltip } from "antd";
import { 
    Expand, 
    Info, 
    Shrink, 
    Rocket, 
    X, 
    Zap, 
    XCircle, 
    Package 
} from "lucide-react";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { getEnvironmentService, getElasticSearchService, getRealtimeService, isAIServiceEnabled } from "../backend";
import DeploymentStatusTag, { isDeploymentStatusFailed, isDeploymentStatusInProgress } from "./DeploymentStatusTag";
import { EnvDeployment, Log } from "../types";
import { mapInfraDeployments } from "../utils/deployments";
import { trackCancelDeployment } from "../backend/tracking/deployment";
import Logs from "./Logs";
import EnvironmentDeploymentResource from "./EnvironmentDeploymentResource";
import { Socket } from "socket.io-client";
import AIAssistantModal from "./modals/AIAssistantModal";
import { GetDeploymentHistoryQueryTypeEnum, StageDeployment } from "@microtica/ms-elasticsearch-sdk";

interface ComponentDeployDetailsDrawerProps {
    type: "build" | "deploy" | string;
    projectId: string;
    envId: string;
    cloudProvider: string;
    iac: string;
    deploymentId: string;
    open?: boolean;
    onClose?: () => void;
}

const ComponentDeployDetailsDrawer = ({
    type,
    projectId,
    envId,
    cloudProvider,
    iac,
    deploymentId,
    open,
    onClose
}: ComponentDeployDetailsDrawerProps) => {
    const [events, setEvents] = useState<string[]>([
        "⏳ \u001b[36mFetching logs...\u001b[0m",
        ""
    ]);
    const [failedEvents, setFailedEvents] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [deploymentDetails, setDeploymentDetails] = useState<EnvDeployment>();
    const [disableCancelDeploymentBtn, setDisableCancelDeploymentBtn] = useState(false);
    const [eventsExpanded, setEventsExpanded] = useState(false);
    const [showUpdatesOnly, setShowUpdatesOnly] = useState(false);
    const [hasUpdates, setHasUpdates] = useState(false);
    const [socket, setSocket] = useState<Socket>();
    const [, setModal] = useState<{
        destroy: () => void;
        update: (configUpdate: ModalFuncProps) => void;
    }>();
    const [openAIAssitantModal, setOpenAIAssitantModal] = useState(false);

    useEffect(() => {
        (async () => {
            const data = await loadData();
            const hasUpdates = data?.resources.some(r => !r.prev.equal);
            setHasUpdates(hasUpdates || false);
            setShowUpdatesOnly(hasUpdates || false);
            setEventsExpanded(data?.resources.length === 0);

            if (iac === "terraform") {
                const socket = getRealtimeService(
                    {
                        projectId,
                        stageId: envId,
                        deploymentId,
                        typeOfLogs: "terraform"
                    }
                );
                setSocket(socket);
            }
        })();

        moment.updateLocale(moment.locale(), { invalidDate: "" });

        const interval = setInterval(async () => {
            const data = await loadData();

            // Stop getting updates if the deployment is already completed
            if (data && !isDeploymentStatusInProgress(data?.status.name!)) {
                clearInterval(interval);
            }
        }, 5000);
        return () => clearInterval(interval);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let allLogs: Log[] = [];
        if (socket !== undefined) {
            socket.on("logs", (logs: Log[]) => {

                allLogs = allLogs.concat(...logs);

                setEvents(allLogs.map(l => `[${l.timestamp}] ${l.message}`));
            });
        }

        return () => { socket?.disconnect() }
    }, [socket]);

    const loadData = async () => {
        try {
            const [
                { data: envDetails },
                { data: { response: deployments } }
            ] = await Promise.all([
                getEnvironmentService().getStage(envId, projectId),
                getElasticSearchService().getDeploymentHistory(
                    projectId,
                    {
                        type: GetDeploymentHistoryQueryTypeEnum.Stage,
                        stageIds: [envId],
                        deploymentId,
                        excludeFields: ["configurations", "kubeConfig", "schemaJSON", "stepVars"]
                    }
                )
            ]);
            const { data: { response: previousDeployment } } = await getElasticSearchService().getDeploymentHistory(
                projectId,
                {
                    type: GetDeploymentHistoryQueryTypeEnum.Stage,
                    stageIds: [envId],
                    deploymentId: deployments[0]?.lastDeploymentId,
                    excludeFields: ["configurations", "kubeConfig", "schemaJSON", "stepVars"]
                }
            )

            const currentDeployment = deployments.find(d => d.deploymentId === deploymentId);
            const infraDeployment = mapInfraDeployments(
                [
                    ...(deployments as StageDeployment[]), 
                    ...(previousDeployment as StageDeployment[])
                ],
                envDetails.lastDeploymentId
            ).find(d => d.id === deploymentId); // TODO

            setDeploymentDetails(infraDeployment);

            if (iac === "cloudformation") {
                // Get the deployment events from the history
                // If not present, get the events from CFN directly
                const historyEvents = [
                    ...(infraDeployment?.status.errors || []).map(e => ({
                        eventId: `${e.logicalResourceId}-${e.statusReason}`,
                        logicalResourceId: e.logicalResourceId,
                        resourceStatus: "DEPLOYMENT_FAILED",
                        resourceType: e.resourceType,
                        statusReason: e.statusReason,
                        timestamp: new Date(infraDeployment?.timestamp!).getTime(),
                        stackId: "",
                        stackName: "",
                        physicalResourceId: "",
                        clientRequestToken: ""
                    })),
                    ...(currentDeployment as StageDeployment)?.logs?.events[`environment-${envId.split("-")[0]}`].events || [],
                ];

                const deploymentEvents = currentDeployment?.status === "DEPLOY_QUEUED" ? [] :
                    historyEvents.length > 0 ? historyEvents :
                        await getEnvironmentService().getStageDeploymentEvents(envId, projectId)
                            .then(res => res.data.events || [])
                            .catch(_ => ([]));

                const sortedEvents = deploymentEvents.sort((d1, d2) => Number(new Date(d1.timestamp)) - Number(new Date(d2.timestamp)));
                setEvents(
                    sortedEvents.map(e =>
                        `${moment(e.timestamp).format()} ${e.resourceStatus?.includes("FAILED") ? "\u001b[31m" : ""}[${e.resourceStatus}] \u001b[0m${e.logicalResourceId}${e.statusReason ? `\n${e.resourceStatus?.includes("FAILED") ? "\u001b[31m" : ""}===> ${e.statusReason}\u001b[0m` : ""}`
                    )
                );
                setFailedEvents(
                    sortedEvents.filter(e => e.resourceStatus?.includes("FAILED")).map(e =>
                        `[${e.resourceStatus}] ${e.logicalResourceId} ===> ${e.statusReason}`
                    ).join("\n")
                )
            }

            return infraDeployment;

        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    const showCancelStageDeploymentConfirm = () => {
        const modal = Modal.confirm({
            title: "Cancel deployment",
            icon: <XCircle size={20} color="#ff4d4f" fill="#ff4d4f" />,
            content: "Are you sure you want to cancel the deployment? This action cannot be undone.",
            okText: "Cancel Deployment",
            okButtonProps: {
                danger: true
            },
            cancelText: "No",
            centered: true,
            async onOk() {
                return handleCancelDeployment();
            },
            onCancel() {
                setModal(undefined);
            }
        });
        setModal(modal);
    }

    const handleCancelDeployment = async () => {
        try {
            setDisableCancelDeploymentBtn(true);
            await getEnvironmentService().cancelDeploy(
                envId,
                projectId,
                deploymentDetails?.status.name === "DEPLOY_QUEUED" ? {
                    deploymentId
                } : undefined
            );
            notification.info({
                message: "Deployment canceled",
                description: "The environment deployment has been canceled"
            });
            trackCancelDeployment(envId);
        } catch (error: any) {
            setDisableCancelDeploymentBtn(false);
            notification.info({
                message: "Deployment canceled error",
                description: error.response?.data?.message || "An unknown erorr has occured"
            });
        }
        setModal(undefined);
    }

    const handleClose = () => {
        onClose?.();
    }

    const Header = () => (
        !deploymentDetails ? <Skeleton /> :
            <div>
                <div className="flex-justify-space-between" style={{ marginBottom: "48px" }}>
                    <Row gutter={[24, 24]}>
                        <Col>
                            <Space direction="vertical">
                                <div style={{ fontSize: "14px", fontWeight: 600 }}>
                                    Environment
                                </div>
                                <div>
                                    {deploymentDetails.envId}
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Space direction="vertical">
                                <div style={{ fontSize: "14px", fontWeight: 600 }}>
                                    Execution time
                                </div>
                                <div>
                                    {
                                        moment(deploymentDetails.timestamp).fromNow()
                                    }
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Space direction="vertical">
                                <div style={{ fontSize: "14px", fontWeight: 600 }}>
                                    Status
                                </div>
                                <div>
                                    <DeploymentStatusTag status={deploymentDetails.status.name} />
                                </div>
                            </Space>
                        </Col>
                    </Row>
                    <Space>
                        {
                            deploymentDetails.status.name === "DEPLOY_QUEUED" ||
                                (["CREATE_IN_PROGRESS", "UPDATE_IN_PROGRESS"].includes(deploymentDetails.status.name!) && cloudProvider === "aws") ?
                                <Button
                                    disabled={disableCancelDeploymentBtn}
                                    danger={true}
                                    onClick={showCancelStageDeploymentConfirm}
                                >
                                    Cancel deployment
                                </Button> : undefined
                        }
                    </Space>
                </div>
            </div >
    )

    const TypeIcon = () => {
        let color: string;
        let icon: JSX.Element;
        
        if (type === "template") {
            color = "#8b5cf6";
            icon = <Package size={16} />;
        } else if (type === "deploy") {
            color = "#077BB5";
            icon = <Rocket size={16} />;
        } else {
            color = "#f59e0b";
            icon = <Zap size={16} />;
        }

        return (
            <div style={{
                flexShrink: 0,
                padding: "7px",
                borderRadius: "6px",
                backgroundColor: `${color}15`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px"
            }}>
                <span style={{ 
                    color: color,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    lineHeight: 1,
                    opacity: 0.75
                }}>
                    {icon}
                </span>
            </div>
        );
    }

    return (
        <Drawer
            title={
                <Space size="middle">
                    <TypeIcon />
                    <div>
                        <div>
                            Deployment
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 400 }}>
                            ID: {deploymentDetails?.id}
                        </div>
                    </div>
                </Space>
            }
            placement="bottom"
            height="100%"
            onClose={handleClose}
            open={open}
            destroyOnClose={true}
            closable={false}
            extra={
                <Button
                    icon={<X size={16} />}
                    onClick={handleClose}
                />
            }
        >
            {
                loading ?
                    <Skeleton /> :
                    <>
                        <Header />
                        {
                            deploymentDetails?.error ?
                                // Deployment error
                                <Alert
                                    message="Deployment error"
                                    description={deploymentDetails.error}
                                    type="error"
                                    showIcon
                                /> :
                                // Deployment details
                                <Row gutter={24}>
                                    {/* Events */}
                                    <Col span={eventsExpanded ? 24 : 18}>
                                        <Logs
                                            title="Deployments logs"
                                            height="calc(100vh - 220px)"
                                            extra={
                                                deploymentDetails?.resources.length! > 0 &&
                                                <Button
                                                    type="link"
                                                    style={{ paddingRight: 0, color: "white" }}
                                                    icon={eventsExpanded ? <Shrink size={14} /> : <Expand size={14} />}
                                                    onClick={() => setEventsExpanded(!eventsExpanded)}
                                                >
                                                    {eventsExpanded ? "Show updates" : "Expand"}
                                                </Button>
                                            }
                                            logs={events}
                                        />
                                    </Col>

                                    {/* Updated resources */}
                                    {
                                        !eventsExpanded &&
                                        <Col span={6} style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 220px)" }}>
                                            <div style={{ fontSize: "18px", fontWeight: 600 }}>
                                                Updates
                                            </div>
                                            <div style={{ margin: "16px 0" }}>
                                                <Tooltip title="Show only the resources that has version or configuration changes in this deployment.">
                                                    <Checkbox
                                                        checked={showUpdatesOnly}
                                                        onChange={(e) => setShowUpdatesOnly(e.target.checked)}
                                                    >
                                                        Only updated resources
                                                    </Checkbox>
                                                    <Info size={14} />
                                                </Tooltip>
                                            </div>
                                            <div style={{ 
                                                flex: 1,
                                                overflowY: "auto",
                                                paddingRight: "8px",
                                                minHeight: 0
                                            }}>
                                                {
                                                    deploymentDetails?.resources
                                                        .filter(r => !showUpdatesOnly ? true : !r.prev?.equal)
                                                        .map(r => (<EnvironmentDeploymentResource envId={envId} deploymentType={deploymentDetails.type} resource={r} key={r.id} />))
                                                }
                                                {
                                                    !hasUpdates && showUpdatesOnly &&
                                                    <div className="text-center">
                                                        No resources with updated version or configuration changes in this deployment.
                                                    </div>
                                                }
                                            </div>
                                        </Col>
                                    }
                                </Row>
                        }

                    </>
            }
            {
                openAIAssitantModal &&
                <AIAssistantModal
                    projectId={projectId}
                    prompt={`${failedEvents.substring(0, 1000)}`}
                    emptyPlaceholder="Analyzing deployment failure... Please wait."
                    open={openAIAssitantModal}
                    onCancel={() => setOpenAIAssitantModal(false)}
                />
            }
        </Drawer>
    )
}

export default ComponentDeployDetailsDrawer;